import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { authConfig } from './auth.config';
import { NotificationService } from '../../@core/services/notification.service';
import { HttpRequesterService } from '../../@core/services/http-requester.service';
import { Router } from '@angular/router';
import { LoadingService } from '../../@core/services/commonServices/loading.service';
import { Store } from '@ngrx/store';
import * as fromActions from '../../@core/store/auth-state/auth.actions';
import { ResetAllStores } from '../../@core/store/app/app.actions';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly oauthService: OAuthService,
    private readonly notificationService: NotificationService,
    private readonly httpRequester: HttpRequesterService,
    private readonly router: Router,
    private readonly store: Store,
    private readonly loadingService: LoadingService,
  ) {
    this.oauthService.configure(authConfig);

    // Default storage ALWAYS sessionStorage (auto logout when browser closes)
    this.oauthService.setStorage(sessionStorage);

    this.oauthService.events.subscribe((event) => {
      if (event.type === 'token_expires') {
        this.oauthService.refreshToken().catch(() => {
          this.notificationService.showError(
            'Session expired. Please log in again.',
          );
          this.logout();
        });
      }

      if (event.type === 'token_received') {
        // console.log('Token received, navigating now...');
        // Check if the login callback happened (code in URL)
        const isLoginRedirect = window.location.search.includes('code=');

        if (isLoginRedirect) {
          this.notificationService.showSuccess(
            'Login successful!',
            'Welcome back',
          );
        }
      }

      if (
        event.type === 'session_terminated' ||
        event.type === 'session_error'
      ) {
        localStorage.removeItem('lastVisitedUrl');
        this.logout();
      }
    });
  }

  // ---------------------------------------------------------------------------------
  // INIT (called once on app startup)
  // ---------------------------------------------------------------------------------

  async init(): Promise<void> {
    try {
      await this.oauthService.loadDiscoveryDocument();
      await this.oauthService.tryLogin();

      if (this.isLoggedIn) {
        await this.loadUserInfoBeforeRouting();
      }
    } catch (err) {
      console.error('Error during OAuth init', err);
    }
  }

  private loadUserInfoBeforeRouting(): Promise<void> {
    const endpoint = 'https://sigma-staging.tamiin.com/connect/userinfo';
    // const endpoint =
    //   'https://innovastrasigma.azurewebsites.net/connect/userinfo';

    this.loadingService.show();

    return new Promise((resolve) => {
      this.httpRequester
        .externalGet(endpoint, {
          headers: {
            'x-error-handled': 'true',
          },
        })
        .subscribe({
          next: (res: any) => {
            this.store.dispatch(
              fromActions.FetchUserInfoSuccessAction({ userInfo: res }),
            );

            const remember = res?.remember_me === 'true';
            localStorage.setItem('remember_me', remember ? 'true' : 'false');

            this.oauthService.setStorage(
              remember ? localStorage : sessionStorage,
            );

            const currentUrl =
              window.location.pathname +
              window.location.search +
              window.location.hash;

            const lastUrl = localStorage.getItem('lastVisitedUrl');

            const isValidAppUrl = (url: string | null): url is string =>
              !!url &&
              url !== '/' &&
              !url.includes('/auth/') &&
              !url.startsWith('/no-access');

            if (isValidAppUrl(currentUrl)) {
              this.router.navigateByUrl(currentUrl);
            } else if (isValidAppUrl(lastUrl)) {
              this.router.navigateByUrl(lastUrl);
            } else {
              this.router.navigateByUrl('/user/dashboard');
            }

            this.loadingService.hide();
            resolve();
          },

          error: () => {
            this.loadingService.hide();
            resolve();
          },
        });
    });
  }
  // ---------------------------------------------------------------------------------
  // LOGIN
  // ---------------------------------------------------------------------------------
  login() {
    try {
      this.oauthService.initLoginFlow();
    } catch (err) {
      this.notificationService.showError(
        'Unable to start login. Please try again.',
      );
    }
  }

  // ---------------------------------------------------------------------------------
  // LOGOUT
  // ---------------------------------------------------------------------------------
  logout() {
    this.loadingService.show();

    const currentUrl = location?.pathname ?? '/';

    //   if (currentUrl && !currentUrl.startsWith("/no-access")) {
    //     localStorage.setItem("lastVisitedUrl", currentUrl);
    // }

    localStorage.removeItem('lastVisitedUrl');
    this.oauthService.logOut();
    this.store.dispatch(ResetAllStores());
    this.notificationService.showInfo('You have been logged out.', 'Goodbye');
  }

  // ---------------------------------------------------------------------------------
  // MANUAL REFRESH
  // ---------------------------------------------------------------------------------
  refresh() {
    this.oauthService
      .refreshToken()
      .then(() => {
        this.notificationService.showSuccess('Session refreshed successfully.');
      })
      .catch(() => {
        this.notificationService.showError(
          'Session refresh failed. Please log in again.',
        );
      });
  }

  // ---------------------------------------------------------------------------------
  // FETCH USER INFO (CRITICAL PART - REMEMBER ME LOGIC HERE)
  // ---------------------------------------------------------------------------------
  getUserInfo() {
    const endpoint = 'https://sigma-staging.tamiin.com/connect/userinfo';
    // const endpoint =
    //   'https://innovastrasigma.azurewebsites.net/connect/userinfo';
    this.loadingService.show();

    this.httpRequester.externalGet(endpoint).subscribe({
      next: (res: any) => {
        this.store.dispatch(
          fromActions.FetchUserInfoSuccessAction({ userInfo: res }),
        );

        // Extract remember_me from backend
        const remember = res?.remember_me === 'true';

        // Save flag
        localStorage.setItem('remember_me', remember ? 'true' : 'false');

        // Critical Step:
        // If remember_me = true → move tokens to localStorage
        // If remember_me = false → move tokens to sessionStorage
        const targetStorage = remember ? localStorage : sessionStorage;
        this.oauthService.setStorage(targetStorage);

        // NAVIGATION
        const lastUrl = localStorage.getItem('lastVisitedUrl');
        const isValidRedirect =
          lastUrl &&
          lastUrl !== '/' &&
          !lastUrl.includes('auth') &&
          !lastUrl.startsWith('/no-access');

        if (isValidRedirect) {
          this.router.navigateByUrl(lastUrl);
        } else {
          this.router.navigate(['/user/dashboard']);
        }

        this.loadingService.hide();
      },
      error: (err) => {
        this.notificationService.showError('Failed to fetch user info.');
        this.loadingService.hide();
      },
    });
  }

  // ---------------------------------------------------------------------------------
  // HELPERS
  // ---------------------------------------------------------------------------------
  get accessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  get isLoggedIn(): boolean {
    return this.oauthService.hasValidAccessToken();
  }
}
